import { IAttributeLocalization } from '../../@types/AttributeTypes';
import { IAttribute } from '../../models/standard/AttributeModel';
import filesApiService from '../filesApiService';

export type AttributeSelectionInput = IAttribute | string | null;

interface ResolveAttributeSelectionOptions {
  attributeCatalog: IAttribute[];
  currentAttributes: IAttribute[];
  locale: string;
  onAttributeCreated?: (attribute: IAttribute) => void;
  selection: AttributeSelectionInput;
}

export const normalizeAttributeName = (value: string): string => value.trim().replace(/\s+/gu, ' ').toLocaleLowerCase();

const getAttributeNames = (attribute: IAttribute): string[] => {
  const names = [attribute.key];
  if (attribute.localization) {
    names.push(...Object.values(attribute.localization).filter((value): value is string => !!value));
  }

  return names;
};

export const findAttributeByName = (attributes: IAttribute[], name: string): IAttribute | undefined => {
  const normalizedName = normalizeAttributeName(name);
  if (!normalizedName) return undefined;

  return attributes.find((attribute) => getAttributeNames(attribute).some((candidate) => normalizeAttributeName(candidate) === normalizedName));
};

export const mergeAttributesById = (...attributeLists: IAttribute[][]): IAttribute[] => {
  const seenIds = new Set<number>();
  const mergedAttributes: IAttribute[] = [];

  attributeLists.flat().forEach((attribute) => {
    if (seenIds.has(attribute.id)) return;

    seenIds.add(attribute.id);
    mergedAttributes.push(attribute);
  });

  return mergedAttributes;
};

export const getLanguageCodeFromLocale = (locale: string): string => locale.split('-')[0].toLocaleLowerCase();

export const buildSingleLocaleAttributeLocalization = (locale: string, value: string): IAttributeLocalization => ({
  [getLanguageCodeFromLocale(locale)]: value.trim(),
});

export const resolveAttributeSelection = async ({
  attributeCatalog,
  currentAttributes,
  locale,
  onAttributeCreated,
  selection,
}: ResolveAttributeSelectionOptions): Promise<IAttribute | null> => {
  if (!selection) return null;

  if (typeof selection !== 'string') {
    return selection;
  }

  const trimmedSelection = selection.trim();
  if (!trimmedSelection) return null;

  const existingAttribute = findAttributeByName([...currentAttributes, ...attributeCatalog], trimmedSelection);
  if (existingAttribute) {
    return existingAttribute;
  }

  const createdAttribute = await filesApiService.createAttribute({
    localization: buildSingleLocaleAttributeLocalization(locale, trimmedSelection),
  });

  onAttributeCreated?.(createdAttribute);
  return createdAttribute;
};
